import React, { useState, useEffect, useCallback, useContext } from 'react';
import { eventsApi } from '../api';
import { UserContext } from '../context/UserContext';
import { isAdmin, isModerator } from '../admin/utils/roles';
import { HrnbTabs, HrnbTab } from '../components/controls/HrnbTabs';
import HrnbContainer, { HrnbRow, HrnbCol } from '../components/controls/HrnbContainer';
import HrnbAlert from '../components/controls/HrnbAlert';
import HrnbSkeleton, { SkeletonCard } from '../components/controls/HrnbSkeleton';
import HrnbButton from '../components/controls/HrnbButton';
import HrnbSearchFilter from '../components/controls/HrnbSearchFilter';
import HrnbSelect from '../components/controls/HrnbSelect';
import { PlusIcon, ClearIcon } from '../icons';
import CreateEventModal from '../components/events/CreateEventModal';
import '../css/pages/EventsPage.css';
import EventCalendar from "../components/events/EventCalendar";
import EventList from "../components/events/EventList";
import EventDetailModal from "../components/events/EventDetailModal";

const TABS = {
    CALENDAR: 'calendar',
    LIST: 'list',
    UPCOMING: 'upcoming',
    MY: 'my',
};

const STATUS_OPTIONS = [
    { value: '', label: 'Все статусы' },
    { value: 'PUBLISHED', label: 'Опубликовано' },
    { value: 'DRAFT', label: 'Черновик' },
    { value: 'CANCELLED', label: 'Отменено' },
    { value: 'COMPLETED', label: 'Завершено' },
];

const TYPE_OPTIONS = [
    { value: '', label: 'Все типы' },
    { value: 'BATTLE_START', label: 'Баттл' },
    { value: 'ROUND_START', label: 'Раунд' },
    { value: 'VOTING_START', label: 'Голосование' },
    { value: 'REGISTRATION_DEADLINE', label: 'Регистрация' },
    { value: 'TRACK_UPLOAD_DEADLINE', label: 'Загрузка' },
    { value: 'LIVE_STREAM', label: 'Стрим' },
    { value: 'MEETING', label: 'Встреча' },
    { value: 'CUSTOM', label: 'Другое' },
];

export default function EventsPage() {
    const { currentUser } = useContext(UserContext);
    const [activeTab, setActiveTab] = useState(TABS.CALENDAR);

    // События
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedEvent, setSelectedEvent] = useState(null);

    // Фильтры и пагинация
    const [searchQuery, setSearchQuery] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterType, setFilterType] = useState('');
    const [page, setPage] = useState(0);
    const [pageSize] = useState(20);
    const [totalElements, setTotalElements] = useState(0);

    // Модальные окна
    const [showCreateModal, setShowCreateModal] = useState(false);

    const canCreateEvent = currentUser && (isAdmin(currentUser) || isModerator(currentUser));

    const fetchEvents = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            let data;
            const commonParams = {
                page,
                size: pageSize,
                ...(filterStatus && { status: filterStatus }),
                ...(filterType && { type: filterType }),
                ...(searchQuery && { search: searchQuery }),
            };

            if (activeTab === TABS.UPCOMING) {
                // Для upcoming используем отдельный endpoint
                data = await eventsApi.getUpcoming();
            } else if (activeTab === TABS.MY) {
                data = await eventsApi.getMyEvents();
            } else {
                data = await eventsApi.list({
                    ...commonParams,
                    sortBy: 'startTime',
                    direction: 'ASC',
                });
            }

            // Нормализация ответа
            const list = Array.isArray(data) ? data : (data?.content || data?.data || []);
            setEvents(list);
            setTotalElements(data?.totalElements || list.length);
        } catch (err) {
            console.error('[EventsPage] Failed to fetch events:', err.message);
            setError('Не удалось загрузить события');
        } finally {
            setLoading(false);
        }
    }, [activeTab, page, pageSize, filterStatus, filterType, searchQuery]);

    useEffect(() => {
        fetchEvents();
    }, [fetchEvents]);

    // Подписка на WebSocket обновления (опционально)
    useEffect(() => {
        // TODO: Добавить WebSocket подписку на события при наличии соответствующего топика
        // const subscription = subscribeToEventUpdates((event) => {
        //     fetchEvents();
        // });
        // return () => { if (subscription) subscription.unsubscribe(); };
    }, []);

    const handleSelectEvent = (event) => {
        setSelectedEvent(event);
    };

    const handleCloseModal = () => {
        setSelectedEvent(null);
    };

    const handleUpdate = () => {
        fetchEvents();
    };

    const handleClearFilters = () => {
        setSearchQuery('');
        setFilterStatus('');
        setFilterType('');
        setPage(0);
    };

    return (
        <HrnbContainer className="events-page">
            <div className="events-page-header">
                <h1 className="events-page-title">Календарь событий</h1>
                {canCreateEvent && (
                    <HrnbButton
                        variant="primary"
                        onClick={() => setShowCreateModal(true)}
                        icon={<PlusIcon />}
                    >
                        Создать событие
                    </HrnbButton>
                )}
            </div>

            <HrnbTabs value={activeTab} onChange={(tab) => { setActiveTab(tab); setPage(0); }} className="events-page-tabs">
                <HrnbTab label="Календарь" value={TABS.CALENDAR} />
                <HrnbTab label="Список" value={TABS.LIST} />
                <HrnbTab label="Предстоящие" value={TABS.UPCOMING} />
                {currentUser && <HrnbTab label="Мои события" value={TABS.MY} />}
            </HrnbTabs>

            {/* Фильтры для списковых вкладок */}
            {(activeTab === TABS.LIST || activeTab === TABS.UPCOMING || activeTab === TABS.MY) && (
                <div className="events-page-filters">
                    <HrnbRow className="events-page-filters-row">
                        <HrnbCol xs={12} md={4}>
                            <HrnbSearchFilter
                                search={searchQuery}
                                onSearchChange={setSearchQuery}
                                onSearchSubmit={() => setPage(0)}
                                placeholder="Поиск по названию..."
                                debounceMs={300}
                            />
                        </HrnbCol>
                        <HrnbCol xs={12} md={3}>
                            <HrnbSelect
                                value={filterType}
                                onChange={(val) => { setFilterType(val); setPage(0); }}
                                options={TYPE_OPTIONS}
                                placeholder="Тип события"
                            />
                        </HrnbCol>
                        <HrnbCol xs={12} md={3}>
                            <HrnbSelect
                                value={filterStatus}
                                onChange={(val) => { setFilterStatus(val); setPage(0); }}
                                options={STATUS_OPTIONS}
                                placeholder="Статус"
                            />
                        </HrnbCol>
                        <HrnbCol xs={12} md={2}>
                            <HrnbButton variant="outline" onClick={handleClearFilters} icon={<ClearIcon />}>
                                Сбросить
                            </HrnbButton>
                        </HrnbCol>
                    </HrnbRow>
                </div>
            )}

            {error && (
                <HrnbAlert type="danger" onClose={() => setError(null)}>
                    {error}
                </HrnbAlert>
            )}

            <div className="events-page-content">
                {loading ? (
                    activeTab === TABS.CALENDAR ? (
                        <div className="event-calendar">
                            <div className="event-calendar-header">
                                <HrnbSkeleton width="40px" height="36px" />
                                <HrnbSkeleton width="180px" height="28px" />
                                <HrnbSkeleton width="40px" height="36px" />
                            </div>
                            <div className="event-calendar-grid">
                                {Array.from({ length: 7 }).map((_, i) => (
                                    <HrnbSkeleton key={`wd-${i}`} height="36px" />
                                ))}
                                {Array.from({ length: 35 }).map((_, i) => (
                                    <HrnbSkeleton key={`day-${i}`} height="60px" />
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="event-list">
                            <div className="event-list-filters">
                                <HrnbSkeleton width="160px" height="40px" />
                                <HrnbSkeleton width="160px" height="40px" />
                                <HrnbSkeleton width="160px" height="40px" />
                            </div>
                            {Array.from({ length: 5 }).map((_, i) => (
                                <SkeletonCard key={i} header={false} lines={2} footer={false} />
                            ))}
                        </div>
                    )
                ) : (
                    <>
                        {(activeTab === TABS.CALENDAR) && (
                            <EventCalendar
                                events={events}
                                onSelectEvent={handleSelectEvent}
                            />
                        )}
                        {(activeTab === TABS.LIST || activeTab === TABS.UPCOMING || activeTab === TABS.MY) && (
                            <EventList
                                events={events}
                                onSelectEvent={handleSelectEvent}
                                loading={loading}
                                page={page}
                                pageSize={pageSize}
                                totalElements={totalElements}
                                onPageChange={setPage}
                            />
                        )}
                    </>
                )}
            </div>

            <EventDetailModal
                event={selectedEvent}
                open={!!selectedEvent}
                onClose={handleCloseModal}
                onUpdate={handleUpdate}
            />

            <CreateEventModal
                open={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onSuccess={handleUpdate}
            />
        </HrnbContainer>
    );
}
